/**
 * <review-summary> — Yotpo's AI review summary ("הלקוחות אומרים") as a product tab and a
 * mobile accordion item (snippets/review-summary.liquid).
 *
 * Yotpo draws the summary inside its reviews widget and re-renders that widget itself, so
 * the node is not moved: once Yotpo has drawn it, its text is copied here (textContent,
 * never HTML), the hidden tab / accordion item named by data-reveal is shown, and the
 * original is hidden (html.has-review-summary). No summary — the tab never appears.
 * "קרא סיכום לפי נושאים" clicks Yotpo's own button, which opens Yotpo's dialog (rendered
 * outside the hidden original).
 * data-default-open="reviews" (the tabs block's "טאב פתוח כברירת מחדל"): once the summary is
 * in, this tab / accordion item is opened — unless the shopper already chose a tab.
 */
if (!customElements.get('review-summary')) {
  const SOURCE = '#yotpo-reviews-main-widget .yotpo-paragraph-summary';

  class ReviewSummary extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.querySelector('[data-summary-topics]')?.addEventListener('click', () => this.openTopics(), { signal });
      // A tab the shopper picked is never taken away by a late-arriving summary.
      document.addEventListener('click', (event) => {
        if (event.target.closest('#productTabs, #productAccordion')) this.touched = true;
      }, { signal, capture: true });
      // Moving the tab (promote) reconnects this element: the summary is already in place.
      if (this.filled) return;
      if (this.fill()) return;

      // Wait for Yotpo: one check per frame however many mutations its render makes.
      this.observer = new MutationObserver(() => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          if (this.fill()) this.observer.disconnect();
        });
      });
      const root = document.querySelector('.yotpo-widget-instance') || document.body;
      this.observer.observe(root, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    fill() {
      const source = document.querySelector(SOURCE);
      const text = source?.querySelector('.yotpo-paragraph-summary-text')?.textContent.trim();
      if (!text) return false;

      this.querySelector('[data-summary-text]').textContent = text;
      const badge = source.querySelector('.yotpo-ai-text')?.textContent.trim();
      const badgeTarget = this.querySelector('[data-summary-badge]');
      if (badge && badgeTarget) badgeTarget.textContent = badge.replace(/\.$/, '');
      const topics = this.querySelector('[data-summary-topics]');
      if (topics) topics.hidden = !source.querySelector('.yotpo-reviews-summary-button');

      document.getElementById(this.dataset.reveal)?.removeAttribute('hidden');
      document.documentElement.classList.add('has-review-summary');
      this.filled = true;
      if (this.dataset.defaultOpen === 'reviews') {
        this.promote();
        if (!this.touched) this.activate();
      }
      return true;
    }

    /** Move this tab / accordion item to the front, keeping "שאלות ותשובות" second. */
    promote() {
      if (this.promoting) return;
      const host = document.getElementById(this.dataset.reveal);
      const list = host?.parentElement;
      if (!list) return;
      this.promoting = true;
      try {
        const faq = list.querySelector('[data-bs-target="#faq"], #headingFaq')?.closest('li, .accordion-item');
        // Only real moves: a move reconnects this element, and an already-first node needs none.
        if (list.firstElementChild !== host) list.prepend(host);
        if (faq && faq !== host && host.nextElementSibling !== faq) host.after(faq);
      } finally {
        this.promoting = false;
      }
    }

    /** Open this tab (desktop) or accordion item (phones) — whichever holds this element. */
    activate() {
      const host = document.getElementById(this.dataset.reveal);
      if (!host) return;
      const tab = host.querySelector('[data-bs-toggle="tab"]');
      const collapse = host.querySelector('.accordion-collapse');
      try {
        if (tab) {
          if (window.bootstrap?.Tab) window.bootstrap.Tab.getOrCreateInstance(tab).show();
          else tab.click();
        } else if (collapse) {
          if (window.bootstrap?.Collapse) window.bootstrap.Collapse.getOrCreateInstance(collapse, { toggle: false }).show();
          else host.querySelector('[data-bs-toggle="collapse"]')?.click();
        }
      } catch (error) {
        // a tab that cannot open is not worth an error
      }
    }

    openTopics() {
      document.querySelector(`${SOURCE} .yotpo-reviews-summary-button`)?.click();
    }
  }

  customElements.define('review-summary', ReviewSummary);
}
